// Shared date/time helpers for the backend's "YYYY-MM-DD HH:MM:SS" UTC
// strings (no timezone marker — produced by _localize_to_utc + strftime in
// the Nextmv Lambdas) and their browser-local display / round-tripping.
// Native Date only: local↔UTC conversion and DST are platform-handled.

const pad2 = (n) => String(n).padStart(2, '0');

// Parse a backend datetime string into a Date. Accepts:
//   - "YYYY-MM-DD HH:MM:SS" (UTC, no marker) — space normalized to "T" + "Z"
//   - ISO 8601 containing "T" and/or a trailing "Z"
// Returns null for anything unparseable, including bare "HH:MM" clocks.
export function parseUtcDateTime(value) {
  if (!value || typeof value !== 'string') return null;
  const normalized =
    /\dT/.test(value) || value.endsWith('Z')
      ? value
      : `${value.replace(' ', 'T')}Z`;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Browser-local zero-padded 24-hour "HH:MM". '' for null.
export function formatLocalHHMM(date) {
  if (!date) return '';
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

// Convert a 24-hour "HH:MM" clock string to a lowercase 12-hour label
// (e.g. "06:00" → "6:00 am", "14:00" → "2:00 pm", "00:00" → "12:00 am").
// Falls back to the input verbatim if it doesn't match the HH:MM shape, so
// callers that pass already-formatted strings still work. Lowercase am/pm
// matches the format OpsOrders returns — the TimeField dropdown labels and
// the route-stop window cells both use this so the surfaces stay
// visually consistent.
export function format12hClock(hhmm) {
  if (typeof hhmm !== 'string' || !/^\d{2}:\d{2}$/.test(hhmm)) return hhmm;
  const [hStr, mStr] = hhmm.split(':');
  const h = Number(hStr);
  const period = h >= 12 ? 'pm' : 'am';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return `${h12}:${mStr} ${period}`;
}

// Browser-local "Jul 2, 1:00 pm" display string — 12-hour to match the
// TimeField dropdown labels. Locale pinned to en-US so tests and UI render
// identically regardless of system locale. '' for null.
export function formatWindowDateTime(date) {
  if (!date) return '';
  const monthDay = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  return `${monthDay}, ${format12hClock(formatLocalHHMM(date))}`;
}

// Date → "YYYY-MM-DD HH:MM:SS" in UTC (space-separated, no T/Z) — the shape
// the Nextmv payload expects for stop time windows. '' for null.
export function toUtcDateTimeString(date) {
  if (!date) return '';
  const ymd = `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(
    date.getUTCDate(),
  )}`;
  const hms = `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:${pad2(
    date.getUTCSeconds(),
  )}`;
  return `${ymd} ${hms}`;
}

// Normalize a backend datetime string ("YYYY-MM-DD HH:MM:SS" or ISO with
// seconds/Z) to the "YYYY-MM-DDTHH:MM" form `<input type="datetime-local">`
// requires. Lexicographic comparison of two normalized values matches
// chronological order. '' for empty input.
export function toDatetimeLocal(value) {
  if (!value) return '';
  return value.replace(' ', 'T').slice(0, 16);
}

// Local Date from a "YYYY-MM-DD" date + "HH:MM" (or "H:MM") clock, both in
// the browser's timezone. Returns null if either part is malformed.
export function combineLocalDateAndTime(dateStr, hhmm) {
  if (!dateStr || !hhmm) return null;
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  const tm = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!dm || !tm) return null;
  const d = new Date(
    Number(dm[1]),
    Number(dm[2]) - 1,
    Number(dm[3]),
    Number(tm[1]),
    Number(tm[2]),
    0,
    0,
  );
  return Number.isNaN(d.getTime()) ? null : d;
}

// Same local clock time on the next calendar day. Goes through the Date
// constructor rather than adding 24h of milliseconds so DST transitions
// don't shift the clock time. Null-safe.
export function addOneDay(date) {
  if (!date) return null;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + 1,
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    0,
  );
}

// "YYYY-MM-DD" → "Mon, Jun 1" for section titles. Parses the parts
// manually so the date is local — new Date('YYYY-MM-DD') is UTC midnight,
// which renders as the previous day in negative-offset timezones. Locale
// pinned to en-US like the other formatters. '' for null/malformed.
export function formatWeekdayMonthDay(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return '';
  const [year, month, day] = parts;
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// "Wednesday, September 9, 2026" for page titles. Locale pinned to en-US
// like the other formatters. '' for null.
export function formatFullDate(date) {
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}
